import { company } from "./company";
import { numberInVietnameseWords } from "./moneyInWords";

export type PrintableInventoryDocument = {
  number: string;
  documentType: "Receipt" | "Issue";
  documentDate: string;
  reason: string;
  warehouseName?: string | null;
  partnerName?: string | null;
  partnerAddress?: string | null;
  partnerPhone?: string | null;
  partnerTaxCode?: string | null;
  relatedCommercialDocumentNumber?: string | null;
  relatedCommercialDocumentDate?: string | null;
  originalDocumentNumber?: string | null;
  productionPlanNumber?: string | null;
  recipient?: string | null;
  recipientDepartmentAddress?: string | null;
  recipientPhone?: string | null;
  issueLocation?: string | null;
  invoiceNumber?: string | null;
  invoiceSeries?: string | null;
  invoiceDate?: string | null;
  status: "Draft" | "Completed" | "Canceled";
  totalAmount: number;
  notes?: string | null;
  deliveryNote?: {
    number: string;
    deliveryDate: string;
    status: "Draft" | "Completed" | "Canceled";
  } | null;
  lines: Array<{
    lineNo: number;
    itemCode?: string | null;
    itemName?: string | null;
    customerItemCode?: string | null;
    customerItemName?: string | null;
    uomName?: string | null;
    lotCode?: string | null;
    expiryDate?: string | null;
    quantity: number;
    unitPrice?: number | null;
    lineTotal?: number | null;
    packagingNote?: string | null;
    packagingQuantity?: number | null;
    packagingUomName?: string | null;
  }>;
};

const escapeHtml = (value: unknown) =>
  String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#039;");

const date = (value?: string | null) =>
  value ? new Date(`${value}T00:00:00`).toLocaleDateString("vi-VN") : "";

const dateParts = (value?: string | null) => {
  if (!value) return { day: "....", month: "....", year: "...." };
  const [year, month, day] = value.slice(0, 10).split("-");
  return { day, month, year };
};

const number = (value?: number | null) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 3 }).format(value ?? 0);

const money = (value?: number | null) =>
  new Intl.NumberFormat("vi-VN", { maximumFractionDigits: 0 }).format(value ?? 0);

const reasonLabel = (reason: string) => ({
  ProductionIssue: "Xuất cho sản xuất",
  SalesIssue: "Xuất bán hàng",
  Return: "Xuất trả lại",
  Adjustment: "Xuất điều chỉnh",
  Other: "Xuất khác"
}[reason] ?? reason);

const commonStyles = `
  @page { size: A4; margin: 12mm 11mm 13mm; }
  * { box-sizing: border-box; }
  body { margin: 0; color: #111; font: 12px "Times New Roman", serif; }
  .sheet { position: relative; width: 100%; min-height: 270mm; }
  .watermark { position: fixed; top: 43%; left: 22%; z-index: -1; color: rgba(180,0,0,.10); font: 700 68px Arial; transform: rotate(-28deg); }
  .top-grid { display: grid; grid-template-columns: 57% 43%; align-items: start; text-align: center; gap: 8px; }
  .top-grid p { margin: 2px 0; }
  .top-grid .left { text-align: left; }
  .top-grid .legal { font-weight: 700; font-size: 13px; }
  .top-grid .motto { font-weight: 700; }
  .form-code { font-weight: 700; font-size: 13px; }
  .italic { font-style: italic; }
  .supplier-block { display: flex; align-items: flex-start; gap: 8px; text-align: left; }
  .supplier-copy { flex: 1; min-width: 0; line-height: 1.3; }
  .logo { flex: 0 0 auto; width: 78px; max-height: 62px; object-fit: contain; margin: 0; }
  .regulation { line-height: 1.3; }
  .document-title { margin: 12px 0 1px; text-align: center; font-size: 19px; }
  .document-number { text-align: center; font-size: 12px; margin: 2px 0; font-style: italic; }
  .date-title { text-align: center; font-style: italic; margin: 2px 0 8px; }
  .invoice-row { display: flex; justify-content: flex-end; margin: 2px 2% 9px 0; }
  .invoice-box { display: inline-block; min-width: 210px; padding: 2px 5px; text-align: left; font-weight: 700; line-height: 1.45; }
  .info-line { margin: 5px 0; line-height: 1.35; }
  .dots { display: inline-block; min-width: 130px; border-bottom: 1px dotted #111; }
  table { width: 100%; border-collapse: collapse; page-break-inside: auto; }
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; page-break-after: auto; }
  th, td { padding: 5px 4px; border: 1px solid #111; text-align: center; vertical-align: middle; }
  th { font-weight: 700; }
  td.left { text-align: left; }
  td.right { text-align: right; }
  .sum-row td { font-weight: 700; }
  .below-table { margin-top: 10px; line-height: 1.65; }
  .sign-date { margin-top: 12px; text-align: right; font-style: italic; }
  .signatures { display: grid; gap: 12px; margin-top: 7px; text-align: center; }
  .signatures.two { grid-template-columns: repeat(2, 1fr); }
  .signatures.five { grid-template-columns: repeat(5, 1fr); }
  .signatures strong { display: block; min-height: 30px; }
  .signatures span { display: block; font-style: italic; font-size: 11px; }
  .signature-space { height: 72px; }
  .actions { position: fixed; right: 16px; bottom: 16px; display: flex; gap: 8px; font-family: Arial, sans-serif; }
  .actions button { padding: 9px 14px; border: 0; border-radius: 7px; color: white; background: #135f80; cursor: pointer; }
  @media print { .actions { display: none; } }
`;

const inventoryRows = (document: PrintableInventoryDocument) => document.lines.map((line) => {
  const useCustomerReference = document.reason === "SalesIssue";
  const itemCode = useCustomerReference ? line.customerItemCode || line.itemCode : line.itemCode;
  const itemName = useCustomerReference ? line.customerItemName || line.itemName : line.itemName;
  return `
  <tr>
    <td>${line.lineNo}</td>
    <td class="left">${escapeHtml(itemName)}</td>
    <td>${escapeHtml(itemCode)}</td>
    <td>${escapeHtml(line.lotCode || "")}</td>
    <td>${date(line.expiryDate)}</td>
    <td>${escapeHtml(line.uomName)}</td>
    <td class="right">${number(line.quantity)}</td>
    <td class="right">${line.unitPrice == null ? "" : money(line.unitPrice)}</td>
    <td class="right">${line.lineTotal == null ? "" : money(line.lineTotal)}</td>
  </tr>`;
}).join("");

const issueRows = (document: PrintableInventoryDocument) => document.lines.map((line) => `
  <tr>
    <td>${line.lineNo}</td>
    <td class="left">${escapeHtml(line.customerItemName || line.itemName)}</td>
    <td>${escapeHtml(line.customerItemCode || line.itemCode)}</td>
    <td>${escapeHtml(line.lotCode || "")}</td>
    <td>${date(line.expiryDate)}</td>
    <td>${escapeHtml(line.uomName)}</td>
    <td class="right">${number(line.quantity)}</td>
    <td>${escapeHtml(line.packagingNote || "")}</td>
  </tr>`).join("");

const deliveryRows = (document: PrintableInventoryDocument) => document.lines.map((line) => `
  <tr>
    <td>${line.lineNo}</td>
    <td>${escapeHtml(line.customerItemCode || line.itemCode)}</td>
    <td class="left">${escapeHtml(line.customerItemName || line.itemName)}</td>
    <td>${escapeHtml(line.lotCode || "")}</td>
    <td>${date(line.expiryDate)}</td>
    <td>${escapeHtml(line.uomName)}</td>
    <td class="right">${number(line.quantity)}</td>
  </tr>`).join("");

const packagingSummary = (document: PrintableInventoryDocument) => {
  const rows = document.lines.filter((line) =>
    line.packagingQuantity != null && Number.isFinite(line.packagingQuantity) && line.packagingQuantity >= 0);
  if (rows.length !== document.lines.length || rows.length === 0)
    return { display: "", words: "........................................................" };
  const total = rows.reduce((sum, line) => sum + Number(line.packagingQuantity || 0), 0);
  const unit = rows[0].packagingUomName || "Thùng";
  const sameUnit = rows.every((line) => (line.packagingUomName || "Thùng") === unit);
  if (!sameUnit) return { display: "", words: "........................................................" };
  const spoken = Number.isInteger(total) ? numberInVietnameseWords(total) : number(total);
  return {
    display: `${number(total)} ${unit.toLocaleLowerCase("vi-VN")}`,
    words: `${spoken} ${unit.toLocaleLowerCase("vi-VN")}`
  };
};

const salesIssueTemplate = (document: PrintableInventoryDocument, logo: string) => {
  const parts = dateParts(document.documentDate);
  const recipientAddress = document.recipientDepartmentAddress || document.partnerAddress || "";
  const recipientPhone = document.recipientPhone || document.partnerPhone || "";
  const packages = packagingSummary(document);
  const sourceNumber = document.originalDocumentNumber || document.relatedCommercialDocumentNumber || "";
  return `
    <div class="top-grid">
      <div class="supplier-block"><img class="logo" src="${logo}" alt="Logo"><div class="supplier-copy"><p><strong>Đơn vị: ${escapeHtml(company.legalName)}</strong></p><p>Địa chỉ: ${escapeHtml(company.address)}</p><p>MST: ${escapeHtml(company.taxCode)}</p></div></div>
      <div class="regulation"><p class="form-code">Mẫu số 02 - VT</p><p class="italic">(Kèm theo Thông tư số 99/2025/TT-BTC</p><p class="italic">ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</p></div>
    </div>
    <h1 class="document-title">PHIẾU XUẤT KHO</h1>
    <div class="date-title">Ngày ${parts.day} tháng ${parts.month} năm ${parts.year}</div>
    <div class="document-number">Số: <strong>${escapeHtml(document.number)}</strong></div>
    <div class="invoice-row"><div class="invoice-box">Số HĐ: ${escapeHtml(document.invoiceNumber || "................................")}<br>Ký hiệu HĐ: ${escapeHtml(document.invoiceSeries || "..........................")}<br>Ngày HĐ: ${escapeHtml(document.invoiceDate ? date(document.invoiceDate) : "..............................")}</div></div>
    <p class="info-line">- <strong>Khách hàng:</strong> ${escapeHtml(document.partnerName || "")}</p>
    <p class="info-line">- <strong>Địa chỉ (bộ phận):</strong> ${escapeHtml(recipientAddress)}</p>
    <p class="info-line">- <strong>Người nhận:</strong> ${escapeHtml(document.recipient || "")}${recipientPhone ? `; &nbsp;<strong>Số điện thoại:</strong> ${escapeHtml(recipientPhone)}` : ""}</p>
    <p class="info-line">- <strong>Lý do xuất kho:</strong> ${sourceNumber ? `Xuất hàng theo PO số: ${escapeHtml(sourceNumber)}` : escapeHtml(reasonLabel(document.reason))}${document.productionPlanNumber ? ` – Lệnh sản xuất ${escapeHtml(document.productionPlanNumber)}` : ""}</p>
    <p class="info-line">- <strong>Xuất tại kho (ngăn lô):</strong> ${escapeHtml(document.warehouseName || "")}</p>
    <p class="info-line">- <strong>Địa điểm:</strong> ${document.issueLocation ? escapeHtml(document.issueLocation) : '<span class="dots"></span>'}</p>
    <table>
      <colgroup><col style="width:4%"><col style="width:31%"><col style="width:15%"><col style="width:12%"><col style="width:9%"><col style="width:7%"><col style="width:9%"><col style="width:13%"></colgroup>
      <thead><tr><th>STT</th><th>Tên hàng hóa</th><th>Mã số</th><th>Lô</th><th>HSD</th><th>ĐVT</th><th>Số lượng</th><th>Ghi chú</th></tr><tr><th>A</th><th>B</th><th>C</th><th>D</th><th>1</th><th>2</th><th>3</th><th>4</th></tr></thead>
      <tbody>${issueRows(document)}<tr class="sum-row"><td colspan="6">Cộng</td><td class="right">${number(document.lines.reduce((sum, line) => sum + line.quantity, 0))}</td><td>${escapeHtml(packages.display)}</td></tr></tbody>
    </table>
    <div class="below-table"><div>- <strong>Bằng chữ:</strong> <em>${escapeHtml(packages.words)}</em></div><div>- <strong>Số chứng từ gốc kèm theo:</strong> ${escapeHtml(sourceNumber)}</div></div>
    <div class="signatures five"><div><strong>Người lập phiếu</strong><span>(Ký, họ tên)</span></div><div><strong>Người nhận hàng</strong><span>(Ký, họ tên)</span></div><div><strong>Thủ kho</strong><span>(Ký, họ tên)</span></div><div><strong>Kế toán trưởng</strong><span>(Ký, họ tên)</span></div><div><strong>Giám đốc</strong><span>(Ký, họ tên)</span></div></div><div class="signature-space"></div>`;
};

const internalIssueTemplate = (document: PrintableInventoryDocument, logo: string) => {
  const parts = dateParts(document.documentDate);
  return `
    <div class="top-grid">
      <div class="supplier-block"><img class="logo" src="${logo}" alt="Logo"><div class="supplier-copy"><p><strong>Đơn vị: ${escapeHtml(company.legalName)}</strong></p><p>Địa chỉ: ${escapeHtml(company.address)}</p><p>MST: ${escapeHtml(company.taxCode)}</p></div></div>
      <div class="regulation"><p class="form-code">Mẫu số 02 - VT</p><p class="italic">(Kèm theo Thông tư số 99/2025/TT-BTC</p><p class="italic">ngày 27 tháng 10 năm 2025 của Bộ trưởng Bộ Tài chính)</p></div>
    </div>
    <h1 class="document-title">PHIẾU XUẤT KHO</h1>
    <div class="date-title">Ngày ${parts.day} tháng ${parts.month} năm ${parts.year}</div>
    <div class="document-number">Số: <strong>${escapeHtml(document.number)}</strong></div>
    <p class="info-line">- <strong>Lý do xuất kho:</strong> ${escapeHtml(reasonLabel(document.reason))}${document.productionPlanNumber ? ` – Lệnh sản xuất ${escapeHtml(document.productionPlanNumber)}` : ""}</p>
    <p class="info-line">- <strong>Xuất tại kho (ngăn lô):</strong> ${escapeHtml(document.warehouseName || "")}</p>
    <p class="info-line">- <strong>Địa điểm:</strong> ${document.issueLocation ? escapeHtml(document.issueLocation) : '<span class="dots"></span>'}</p>
    <table>
      <colgroup><col style="width:4%"><col style="width:25%"><col style="width:12%"><col style="width:12%"><col style="width:10%"><col style="width:7%"><col style="width:10%"><col style="width:10%"><col style="width:10%"></colgroup>
      <thead><tr><th>STT</th><th>Tên hàng hóa</th><th>Mã số</th><th>Lô</th><th>HSD</th><th>ĐVT</th><th>Số lượng</th><th>Đơn giá</th><th>Thành tiền</th></tr></thead>
      <tbody>${inventoryRows(document)}<tr class="sum-row"><td colspan="6">Cộng</td><td>${number(document.lines.reduce((sum, line) => sum + line.quantity, 0))}</td><td></td><td>${document.totalAmount ? money(document.totalAmount) : ""}</td></tr></tbody>
    </table>
    <div class="below-table"><div>- Tổng số tiền (viết bằng chữ): ...................................................................................................................</div><div>- Số chứng từ gốc kèm theo: ${escapeHtml(document.originalDocumentNumber || document.relatedCommercialDocumentNumber || "")}</div></div>
    <div class="signatures five"><div><strong>Người lập phiếu</strong><span>(Ký, họ tên)</span></div><div><strong>Người nhận hàng</strong><span>(Ký, họ tên)</span></div><div><strong>Thủ kho</strong><span>(Ký, họ tên)</span></div><div><strong>Kế toán trưởng</strong><span>(Ký, họ tên)</span></div><div><strong>Giám đốc</strong><span>(Ký, họ tên)</span></div></div><div class="signature-space"></div>`;
};

const deliveryTemplate = (document: PrintableInventoryDocument, logo: string) => {
  const deliveryDate = document.deliveryNote?.deliveryDate ?? document.documentDate;
  const parts = dateParts(deliveryDate);
  const recipientAddress = document.recipientDepartmentAddress || document.partnerAddress || "";
  const recipientPhone = document.recipientPhone || document.partnerPhone || "";
  const sourceNumber = document.originalDocumentNumber || document.relatedCommercialDocumentNumber || "";
  return `
    <div class="top-grid">
      <div class="supplier-block"><img class="logo" src="${logo}" alt="Logo"><div class="supplier-copy"><p><strong>Tên NCC: ${escapeHtml(company.legalName)}</strong></p><p>Địa chỉ: ${escapeHtml(company.address)}</p><p>MST: ${escapeHtml(company.taxCode)}</p></div></div>
      <div class="regulation"><p class="legal">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p><p class="motto">Độc lập - Tự do - Hạnh phúc</p></div>
    </div>
    <h1 class="document-title">BIÊN BẢN BÀN GIAO HÀNG HÓA</h1>
    <div class="document-number">Số: <strong>${escapeHtml(document.deliveryNote?.number || "")}</strong></div>
    <div class="invoice-row"><div class="invoice-box">Số HĐ: ${escapeHtml(document.invoiceNumber || "................................")}<br>Ký hiệu HĐ: ${escapeHtml(document.invoiceSeries || "..........................")}<br>Ngày HĐ: ${escapeHtml(document.invoiceDate ? date(document.invoiceDate) : "..............................")}</div></div>
    <p class="info-line"><strong>BÊN A (Bên nhận hàng):</strong> ${escapeHtml(document.partnerName || "")}</p>
    <p class="info-line">- Địa chỉ: ${escapeHtml(recipientAddress)}</p>
    <p class="info-line">- Điện thoại: ${escapeHtml(recipientPhone)}</p>
    <p class="info-line"><strong>BÊN B (Bên giao hàng):</strong> ${escapeHtml(company.legalName)}</p>
    <p class="info-line">- Địa chỉ: ${escapeHtml(company.address)}</p>
    <p class="info-line">- Điện thoại: ${escapeHtml(company.phone)}</p>
    <p class="info-line">- Số chứng từ gốc kèm theo: <strong>${escapeHtml(sourceNumber)}</strong></p>
    <p class="info-line">Hai bên cùng thống nhất số lượng giao hàng như sau:</p>
    <table>
      <colgroup><col style="width:5%"><col style="width:14%"><col style="width:31%"><col style="width:14%"><col style="width:12%"><col style="width:10%"><col style="width:14%"></colgroup>
      <thead><tr><th>STT</th><th>Mã hàng</th><th>Tên hàng</th><th>Số lô</th><th>Hạn sử dụng</th><th>Đơn vị tính</th><th>Số lượng</th></tr></thead>
      <tbody>${deliveryRows(document)}<tr class="sum-row"><td colspan="6">Cộng</td><td>${number(document.lines.reduce((sum, line) => sum + line.quantity, 0))}</td></tr></tbody>
    </table>
    <p class="info-line">Biên bản được lập thành 02 bản có giá trị như nhau; mỗi bên giữ 01 bản.</p>
    <div class="sign-date">Ngày ${parts.day} tháng ${parts.month} năm ${parts.year}</div>
    <div class="signatures two"><div><strong>ĐẠI DIỆN BÊN A</strong><span>(Ký, ghi rõ họ tên và đóng dấu nếu có)</span></div><div><strong>ĐẠI DIỆN BÊN B</strong><span>(Ký, ghi rõ họ tên và đóng dấu nếu có)</span></div></div><div class="signature-space"></div>`;
};

const receiptTemplate = (document: PrintableInventoryDocument, logo: string) => `
  <div class="top-grid"><div class="left"><p><strong>${escapeHtml(company.legalName)}</strong></p><p>${escapeHtml(company.address)}</p><p>MST: ${escapeHtml(company.taxCode)}</p><img class="logo" src="${logo}" alt="Logo"></div><div><p class="legal">CỘNG HÒA XÃ HỘI CHỦ NGHĨA VIỆT NAM</p><p class="motto">Độc lập - Tự do - Hạnh phúc</p><h1>PHIẾU NHẬP KHO</h1><div class="date-title">Số: ${escapeHtml(document.number)} – Ngày ${date(document.documentDate)}</div></div></div>
  <p class="info-line">Nhà cung cấp/Đối tượng: <strong>${escapeHtml(document.partnerName || "")}</strong></p><p class="info-line">Nhập tại kho: <strong>${escapeHtml(document.warehouseName || "")}</strong></p>
  <table><thead><tr><th>STT</th><th>Tên hàng hóa</th><th>Mã số</th><th>Lô</th><th>HSD</th><th>ĐVT</th><th>Số lượng</th><th>Đơn giá</th><th>Thành tiền</th></tr></thead><tbody>${inventoryRows(document)}</tbody></table>
  <div class="signatures two"><div><strong>NGƯỜI LẬP PHIẾU</strong><span>(Ký, họ tên)</span></div><div><strong>THỦ KHO</strong><span>(Ký, họ tên)</span></div></div>`;

export function printInventoryDocument(
  document: PrintableInventoryDocument,
  kind: "inventory" | "delivery",
  saveAsPdf = false
) {
  const printWindow = window.open("", "_blank", "width=1050,height=760");
  if (!printWindow) {
    window.alert("Trình duyệt đang chặn cửa sổ in. Hãy cho phép cửa sổ bật lên rồi thử lại.");
    return;
  }
  const title = kind === "delivery"
    ? "Biên bản bàn giao hàng hóa"
    : document.documentType === "Issue" ? "Phiếu xuất kho" : "Phiếu nhập kho";
  const logo = new URL(company.logoPath, window.location.origin).href;
  const body = kind === "delivery"
    ? deliveryTemplate(document, logo)
    : document.documentType === "Issue"
      ? document.reason === "SalesIssue" ? salesIssueTemplate(document, logo) : internalIssueTemplate(document, logo)
      : receiptTemplate(document, logo);
  const draft = document.status === "Draft" ? '<div class="watermark">BẢN NHÁP</div>' : "";

  printWindow.document.write(`<!doctype html><html lang="vi"><head><meta charset="utf-8"><title>${escapeHtml(title)} ${escapeHtml(document.number)}</title><style>${commonStyles}</style></head><body><main class="sheet">${draft}${body}<div class="actions"><button onclick="window.print()">In / Lưu PDF</button><button onclick="window.close()">Đóng</button></div></main></body></html>`);
  printWindow.document.close();
  printWindow.focus();
  window.setTimeout(() => {
    if (saveAsPdf) printWindow.alert("Trong mục Máy in, chọn “Microsoft Print to PDF” hoặc “Save as PDF”, sau đó nhấn Lưu.");
    printWindow.print();
  }, 450);
}
